import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import _ from 'lodash'
import styled from 'particles/themes/styled'
import { theme } from 'theme'
import { createGetRequestsAreLoading, selectTeam, State } from 'state'
import { STATE_KEYS } from 'state/requests'
import {
    TeamActions,
    getTeam,
    getRoles,
    RolesState,
    selectPermissionsByCode,
    selectMapPermissionsByCode,
} from 'state/teams'
import { GridColDef, GridCellParams } from '@mui/x-data-grid-pro'
import { CustomHeader } from 'components/Shared/MuiTable/CustomHeader'
import { TitleCell } from 'components/Shared/MuiTable/Cell/TitleCell'
import { CheckBoxCell } from 'components/Shared/MuiTable/Cell/CheckBoxCell'
import {
    updateRoles,
    selectNewRoles,
    selectOriginRoles,
    addRole,
    editRoleName,
    deleteRole,
    selectPermissions,
    getTeamPermissions,
    UpdateRoleActions,
    SubmitRolesPermissions,
    updateRolesPermissions,
    PermissionsState,
    PermissionEntitiesState,
} from 'state/teams'
import { useCheckTeamPermission, usePermission } from 'hooks/permissions'
import TeamRestricted from 'components/Shared/Permission/TeamRestricted'
import styles from './styles'
import Header, { SwitchViewType } from 'components/Team/common/Header'
import RolesTable from './RolesTable'
import PopUpInput from 'components/Shared/PopUpInput'
import { useWindowSize, useIsRoleManagementValuesChanged, useNewTranslation } from 'hooks'
import * as yup from 'yup'
import { getPermissionGroupTranslation, getPermissionTranslation } from './translation'

import {
    CampaignSubmission,
    CampaignManagement,
    OtherExternalTeamManagement,
    CampaignStatusControl,
} from 'components/Shared/Permission/enums'
import { UnchangeablePermissionsCodes, UnchangeablePermissionsGroup } from '__mocks__/data/roles'
import { selectIsInternalUser, selectProfileRoleData } from 'state/profile'
import RectangleButton from 'particles/Button/RectangleButton'
import Modal from 'particles/Modal'
import Text from 'particles/Text'
import { MODAL_TYPE, ModalState, PredefinedModal } from 'components/Team/common/Modals'
import { getTeamTypeOptions } from '../utils'

export interface RowsProps {
    id: string
    title: {
        value: string
        isFirstTitle: boolean
    }
}

interface RolesProps {
    className?: string
}
interface ShowInputProps {
    isShow: boolean
    id?: string | null
    placeHolder: string
    popupTitle: string
    inputTitle: string
    submitText: string
    setShowPopup: (value: any) => any
    handleSubmit: (value: any) => any
}
interface RolesRow {
    id: string
    title: {
        isFirstTitle: boolean
        value: string
    }
}
enum ConfirmPopupState {
    NOT_SAVE,
    DELETE_ROLE,
    DELETE_USED_ROLE,
}

const Roles: React.FC<RolesProps> = ({ className }) => {
    // TODO hide Add/Update/Delete Role
    const access = false
    const [showPopInput, setShowPopInput] = useState<ShowInputProps>({
        isShow: false,
        id: null,
        placeHolder: '',
        popupTitle: '',
        inputTitle: '',
        submitText: '',
        setShowPopup: () => null,
        handleSubmit: () => null,
    })
    const history = useHistory()
    const [showConfirm, setShowConfirm] = useState({
        isShow: false,
        popupStatus: ConfirmPopupState.NOT_SAVE,
        deleRoleId: '',
        directPath: SwitchViewType.ROLE_MANAGEMENT,
    })
    const dispatch = useDispatch()
    const [isExistRole, setIsExistRole] = useState(false)
    const [t] = useNewTranslation()
    const { uuid } = useParams<{ uuid: string }>()
    const team = useSelector(selectTeam(uuid as string))
    const isEditAccess = useCheckTeamPermission(team)
    const newRoles = useSelector(selectNewRoles)
    const originRoles = useSelector(selectOriginRoles)
    const permissions = useSelector(selectPermissions)
    const isInternalUser = useSelector(selectIsInternalUser)
    const windowWidth = useWindowSize()
    const [openModal, setOpenModal] = useState<ModalState>({ team: undefined, type: undefined })
    const isLoading = useSelector(createGetRequestsAreLoading([STATE_KEYS.GetTeam]))
    const showPrivateCampaignBudgetPermission = useSelector((state: State) =>
        selectPermissionsByCode(state, {
            codes: [CampaignManagement.ShowPrivateCampaignBudget],
        }),
    )[0]
    const campaignManagementPermissions = useSelector((state: State) =>
        selectPermissionsByCode(state, { codes: Object.values(CampaignManagement) }),
    )
    const mappedPermissionsByCode = useSelector(selectMapPermissionsByCode)
    const userRole = useSelector(selectProfileRoleData)
    const isNotEdited = !useIsRoleManagementValuesChanged()
    const canEditTeamType = usePermission(OtherExternalTeamManagement.ManageRoles) && !team?.is_internal
    const teamTypeOptions = getTeamTypeOptions(t)
    const canSeePartipostFee = (() => {
        if (team?.is_internal) {
            return false
        }

        if (isInternalUser) {
            return true
        }

        return team.type === 'saas'
    })()

    const getIsPermissionDisabled = useCallback(
        (permissionCode: string, role: RolesState): { disabled: boolean; reason?: string } => {
            // Add condition here if a permission needs to be disabled based on other permission
            // The first condition to except the ShowPrivateCampaignBudget is to enable toggle on and off for the rest of the campaign permissions
            // ^ It's because we moved the permission to under the same group
            if (
                !team?.is_internal &&
                Object.values(CampaignManagement).includes(permissionCode as CampaignManagement) &&
                permissionCode !== CampaignManagement.ShowPrivateCampaignBudget
            ) {
                return {
                    disabled:
                        !showPrivateCampaignBudgetPermission ||
                        (showPrivateCampaignBudgetPermission &&
                            !role.permissions.includes(showPrivateCampaignBudgetPermission.uuid)),
                }
            } else if (permissionCode === CampaignManagement.ShowPrivateCampaignBudget) {
                //check the permissions except the show private campaign budget to enable toggle off if any of the rest permissions not in role
                const checkPermissions = campaignManagementPermissions.filter(
                    permission => permission.code !== CampaignManagement.ShowPrivateCampaignBudget,
                )
                return {
                    disabled: checkPermissions.findIndex(permission => role.permissions.includes(permission.uuid)) > -1,
                }
            } else if (permissionCode === CampaignSubmission.ReviewDirectly) {
                // disable Campaign Submission Review Directly if Campaign Submission Review Indirectly is enabled
                const reviewIndirectlyPermissionUuid = mappedPermissionsByCode.get(
                    CampaignSubmission.ReviewIndirectly,
                )?.uuid
                if (reviewIndirectlyPermissionUuid && role.permissions.includes(reviewIndirectlyPermissionUuid)) {
                    return {
                        disabled: true,
                        reason: t('manageTeams.rolesAndPermissions.reviewSubmissionDirectlyExclusiveTooltip'),
                    }
                }
            } else if (permissionCode === CampaignSubmission.ReviewIndirectly) {
                // disable Campaign Submission Review Indirectly if Campaign Submission Review Directly is enabled
                const reviewDirectlyPermissionUuid = mappedPermissionsByCode.get(
                    CampaignSubmission.ReviewDirectly,
                )?.uuid
                if (reviewDirectlyPermissionUuid && role.permissions.includes(reviewDirectlyPermissionUuid)) {
                    return {
                        disabled: true,
                        reason: t('manageTeams.rolesAndPermissions.reviewSubmissionIndirectlyExclusiveTooltip'),
                    }
                }
            } else if (permissionCode === CampaignStatusControl.PayCampaign) {
                return {
                    disabled: !canEditTeamType,
                }
            }
            return { disabled: false }
        },
        [team, showPrivateCampaignBudgetPermission, campaignManagementPermissions, mappedPermissionsByCode],
    )

    const handleLeavePageWithoutSave = (viewType: SwitchViewType) => {
        return setShowConfirm({
            ...showConfirm,
            isShow: true,
            popupStatus: ConfirmPopupState.NOT_SAVE,
            directPath: viewType,
        })
    }

    const handleSwitchViewType = (type: SwitchViewType) => {
        switch (type) {
            case SwitchViewType.MEMBERS:
                history.push(`/team/${uuid}/members`)
                break
            case SwitchViewType.ROLE_MANAGEMENT:
                history.push(`/teams/${uuid}/roles`)
                break
            case SwitchViewType.BRAND_ACCESS:
                history.push(`/team/${uuid}/brands`)
                break
            case SwitchViewType.BUSINESS_DETAILS:
                history.push(`/teams/${uuid}/business-details`)
                break
        }
    }

    const handleAddNewRole = (roleName: string) => {
        dispatch(
            addRole({
                teamUuid: uuid,
                name: roleName,
                description: null,
                onSuccessCallback: () => {
                    setShowPopInput({ ...showPopInput, isShow: false })
                    setIsExistRole(false)
                },
                onErrorCallback: () => {
                    setIsExistRole(true)
                },
            }),
        )
    }
    const handleEditRoleName = useCallback(
        (id: string, roleName: string) => {
            dispatch(
                editRoleName({
                    teamUuid: uuid,
                    roleId: id,
                    roleName,
                    onSuccessCallback: () => {
                        setShowPopInput(current => ({ ...current, isShow: false }))
                        setIsExistRole(false)
                    },
                    onErrorCallback: () => {
                        setIsExistRole(true)
                    },
                }),
            )
        },
        [dispatch, uuid],
    )
    const handleDeleteConfirmPopup = useCallback(
        (id: string) => {
            const selectRole = newRoles.find(role => role.uuid === id)
            if (!selectRole) return
            if (selectRole.member_count > 0) {
                setShowConfirm(current => ({
                    ...current,
                    isShow: true,
                    popupStatus: ConfirmPopupState.DELETE_USED_ROLE,
                    deleRoleId: id,
                }))
            } else {
                setShowConfirm(current => ({
                    ...current,
                    isShow: true,
                    popupStatus: ConfirmPopupState.DELETE_ROLE,
                    deleRoleId: id,
                }))
            }
        },
        [newRoles],
    )

    const handleDeleteRole = (id: string | null) => {
        if (!id) return
        dispatch(deleteRole({ teamUuid: uuid, roleId: id }))
        setShowConfirm({ ...showConfirm, isShow: false })
    }
    const handleSubmitPermissions = useCallback(() => {
        const submitPermissions: SubmitRolesPermissions[] = []
        let refreshUserProfile = false
        newRoles.forEach(role => {
            if (!refreshUserProfile) {
                refreshUserProfile = userRole?.uuid === role.uuid
            }
            const rolePermissions: { uuid: string }[] = []
            role.permissions.forEach((permission: string) => {
                const submitPermission = {
                    uuid: permission,
                }
                if (!UnchangeablePermissionsCodes.includes(permission)) {
                    rolePermissions.push(submitPermission)
                }
            })

            const roleDetail = {
                role: role.uuid,
                permissions: rolePermissions,
            }
            submitPermissions.push(roleDetail)
        })
        dispatch(updateRolesPermissions({ teamUuid: uuid, teamRoles: submitPermissions, refreshUserProfile }))
    }, [newRoles, userRole])

    const onCheckPermission = useCallback(
        (roleId: string, permissionId: string) => {
            dispatch(updateRoles({ roleId, permissionId, type: UpdateRoleActions.UPDATE_PERMISSIONS }))
        },
        [dispatch],
    )
    const ROLE_SETTINGS = useMemo(
        () => [
            {
                id: 'edit',
                text: "Edit Role's Name",
                value: null,
                color: theme.colors.governorBay,
                onClick: (id: string, placeHolder: string) => {
                    setShowPopInput(current => ({
                        ...current,
                        isShow: true,
                        id: id,
                        placeHolder: placeHolder,
                        inputTitle: 'Role Name',
                        submitText: 'Save Changes',
                        popupTitle: "Edit Role's Name",
                        setShowPopup: (value: boolean) => {
                            setShowPopInput(curr => ({ ...curr, isShow: value }))
                        },
                        handleSubmit: (value: string) => {
                            handleEditRoleName(id, value)
                        },
                    }))
                },
            },
            {
                id: 'delete',
                text: 'Delete Role',
                value: 'Delete',
                color: theme.colors.monza,
                onClick: (id: string) => {
                    handleDeleteConfirmPopup(id)
                },
            },
        ],
        [handleDeleteConfirmPopup, handleEditRoleName],
    )
    const rolesColumns = useMemo((): GridColDef[] => {
        const cols = [
            {
                field: 'title',
                headerName: t('manageTeams.rolesAndPermissions.permissionsTableLabel'),
                width: 260,
                cellClassName: 'titleCell',
                renderCell: function CellText(params: GridCellParams): React.ReactElement {
                    const permissionLabel = params.row.title.isFirstTitle
                        ? getPermissionGroupTranslation(t, params.row.title.value, team?.is_internal)
                        : getPermissionTranslation(t, params.row.title.value, team?.is_internal)
                    return (
                        <TitleCell
                            value={permissionLabel.description}
                            isFirstTitle={params.row.title.isFirstTitle}
                            isPopupInfo
                            popupInfoContent={permissionLabel.info}
                        />
                    )
                },
                sortable: false,
            },
        ]
        if (newRoles.length > 0) {
            newRoles.forEach((item: RolesState, index: number) => {
                const insertCol = {
                    field: item.uuid,
                    headerName: item.name,
                    width: 160,
                    sortable: false,
                    cellClassName: 'title',
                    renderHeader: function CellText(): React.ReactElement {
                        return <CustomHeader value={item.name} id={item.uuid} />
                    },
                    renderCell: function CellText(params: GridCellParams): React.ReactElement {
                        const unChangeable = UnchangeablePermissionsCodes.includes(params.row.title.value)
                        const isChecked = item.permissions.includes(params.row.id)
                        const permissionCode = params.row.title.value
                        const { disabled, reason: disabledReason } = getIsPermissionDisabled(permissionCode, item)

                        return (
                            <CheckBoxCell
                                isShowBlank={params.row.title.isFirstTitle}
                                isChecked={isChecked}
                                disabled={disabled}
                                isPopupInfo={Boolean(disabledReason)}
                                popupInfoContent={disabledReason}
                                onCheck={() => {
                                    onCheckPermission(item.uuid, params.row.id)
                                }}
                                access={isEditAccess && !unChangeable}
                            />
                        )
                    },
                }
                cols.splice(index + 1, 0, insertCol)
            })
        }
        return cols
    }, [team, newRoles, t, isEditAccess, onCheckPermission, ROLE_SETTINGS, getIsPermissionDisabled])

    const rolesRows = useMemo((): RowsProps[] => {
        const displayRows: RolesRow[] = []
        const displayPermission = permissions
        displayPermission.forEach((item: PermissionEntitiesState) => {
            if (!UnchangeablePermissionsGroup.includes(item.group_name)) {
                const titleRow = {
                    id: item.group_name,
                    title: {
                        value: item.group_name,
                        isFirstTitle: true,
                    },
                }
                displayRows.push(titleRow)
                item.permissions.map((item: PermissionsState) => {
                    if (!UnchangeablePermissionsCodes.includes(item.code)) {
                        displayRows.push({
                            id: item.uuid,
                            title: {
                                value: item.code,
                                isFirstTitle: false,
                            },
                        })
                    }
                })
            }
        })
        return displayRows
    }, [team, permissions])

    const modifyRoleNameValidation = useMemo(() => {
        return yup.object().shape({
            inputValues: yup.string(),
        })
    }, [])

    const confirmPopupTitle = useMemo(() => {
        switch (showConfirm.popupStatus) {
            case ConfirmPopupState.NOT_SAVE:
                return t('errorMessage.general.cantSaveChanges')
            case ConfirmPopupState.DELETE_USED_ROLE:
                return t('modalWindow.confirmationMessage.deleteUsedRoleTitle', 'Before you delete...')
            case ConfirmPopupState.DELETE_ROLE:
                return t('modalWindow.confirmationMessage.deleteRoleFromTeamTitle', {
                    defaultValue: 'Delete “role name” from {{teamName}}?',
                    teamName: team.name,
                })
        }
    }, [team, showConfirm])

    const deleRoleMemberCount = useMemo(() => {
        if (!showConfirm.deleRoleId) return 0
        const selectRole = originRoles.find(role => role.uuid === showConfirm.deleRoleId)
        if (!selectRole) return 0
        return selectRole.member_count
    }, [showConfirm, originRoles])

    useEffect(() => {
        dispatch(getTeam({ teamUuid: uuid }))
        dispatch(getRoles({ teamUuid: uuid }))
        dispatch(getTeamPermissions({ teamUuid: uuid }))
        setShowPopInput({
            ...showPopInput,
            placeHolder: 'New role',
            popupTitle: 'Create new role',
            inputTitle: 'Role Name',
            submitText: 'Add',
            setShowPopup: (value: boolean) => {
                setShowPopInput({ ...showPopInput, isShow: value })
            },
            handleSubmit: handleAddNewRole,
        })
        return (): void => {
            dispatch({ type: TeamActions.CLEAR_TEAM_MEMBER_LIST })
            dispatch({ type: TeamActions.CLEAR_TEAM_LIST })
            dispatch({ type: TeamActions.CLEAR_ROLES })
            dispatch({ type: TeamActions.CLEAR_TEAM_PERMISSIONS })
        }
    }, [uuid])

    const onCloseModal = (): void => setOpenModal({ team: openModal.team, type: undefined })

    if (!team) {
        return null
    }

    return (
        <div className={className}>
            <Header
                isNotEdited={isNotEdited}
                handleLeavePageWithoutSave={handleLeavePageWithoutSave}
                switchProps={{
                    currentViewType: 'roles',
                }}
            />
            <TeamHeader>
                <Text scale="bodyS500">{t('manageTeams.allTeams.teamTypeTitle')}</Text>
                <div className="team-type-content">
                    <div className="details">
                        <Text scale="H3">{teamTypeOptions[team.type]?.title}</Text>
                        {canSeePartipostFee && (
                            <Text scale="bodyS400" color="secondary">
                                {t('manageTeams.externalTeam.defaultPartipostFee', {
                                    defaultPartipostFee: (team.service_fee_percentage ?? '-') + '%',
                                })}
                            </Text>
                        )}
                    </div>
                    {canEditTeamType && (
                        <RectangleButton
                            size="l"
                            label={t('button.edit')}
                            onClick={() => setOpenModal({ team: team, type: MODAL_TYPE.EDIT_TEAM_TYPE })}
                        />
                    )}
                </div>
            </TeamHeader>

            <PredefinedModal onClose={onCloseModal} {...openModal} />

            {access && (
                <RectangleButton
                    label={t('button.createNewRole', 'Create new role')}
                    size="s"
                    icon="plus"
                    form="outlined"
                    onClick={() => {
                        setShowPopInput({
                            ...showPopInput,
                            isShow: true,
                            placeHolder: 'New role',
                            popupTitle: 'Create new role',
                            inputTitle: 'Role Name',
                            submitText: 'Add',
                            setShowPopup: (value: boolean) => {
                                setShowPopInput({ ...showPopInput, isShow: value })
                            },
                            handleSubmit: handleAddNewRole,
                        })
                    }}
                />
            )}

            <div className={`role-table ${access && 'margin-top-none'}`}>
                <RolesTable
                    isLoading={isLoading}
                    rows={rolesRows}
                    columns={rolesColumns}
                    pinnedColumns={windowWidth && windowWidth < 540 ? {} : { left: ['title'] }}
                    hideFooter={!access}
                    access={access}
                    hasFooterButtons={isEditAccess}
                />
                <TeamRestricted selectedTeam={team}>
                    <div className="table-button">
                        <RectangleButton
                            form="text"
                            size="m"
                            color="neutral"
                            label={t('button.cancel')}
                            onClick={() => dispatch(getRoles({ teamUuid: uuid }))}
                        />

                        <RectangleButton
                            form="filled"
                            size="m"
                            label={t('button.saveChanges')}
                            isDisabled={isNotEdited}
                            onClick={handleSubmitPermissions}
                        />
                    </div>
                </TeamRestricted>
            </div>

            {showPopInput.isShow && (
                <PopUpInput
                    placeHolder={showPopInput.placeHolder}
                    popupTitle={showPopInput.popupTitle}
                    inputTitle={showPopInput.inputTitle}
                    submitText={showPopInput.submitText}
                    setShowPopup={showPopInput.setShowPopup}
                    handleSubmit={showPopInput.handleSubmit}
                    isWaitForResponseClose
                    validationSchema={modifyRoleNameValidation}
                    isShowCustomError={isExistRole}
                    customErrorMessage={t(
                        'errorMessage.validation.roleNamesAlreadyTaken',
                        'This role name has been taken in your team.',
                    )}
                />
            )}
            {showConfirm.isShow && (
                <Modal
                    title={confirmPopupTitle}
                    size="s"
                    isClosable={false}
                    primaryButtonProps={{
                        label:
                            showConfirm.popupStatus === ConfirmPopupState.NOT_SAVE
                                ? t('button.yesSaveChanges')
                                : t('button.delete'),
                    }}
                    secondaryButtonProps={{
                        label: t('button.noLeavePage'),
                    }}
                    onConfirm={() => {
                        if (showConfirm.popupStatus === ConfirmPopupState.NOT_SAVE) {
                            handleSubmitPermissions()
                            setShowConfirm({ ...showConfirm, isShow: false })
                        } else {
                            handleDeleteRole(showConfirm.deleRoleId)
                        }
                    }}
                    onClose={() => {
                        if (showConfirm.popupStatus === ConfirmPopupState.NOT_SAVE) {
                            handleSwitchViewType(showConfirm.directPath)
                        } else {
                            return
                        }
                    }}
                >
                    <Text scale="bodyM400">
                        {showConfirm.popupStatus === ConfirmPopupState.NOT_SAVE &&
                            t('modalWindow.saveChangesBeforeLeavingPageSubtitle')}
                        {showConfirm.popupStatus === ConfirmPopupState.DELETE_ROLE &&
                            t(
                                'paroles.table.confirm.content.deleteRole',
                                "Are you sure you want to delete? You can't undo after deleting.",
                            )}
                        {showConfirm.popupStatus === ConfirmPopupState.DELETE_USED_ROLE &&
                            t('modalWindow.confirmationMessage.deleteUsedRoleSubtitle', {
                                deleteRoleMemberCount: deleRoleMemberCount,
                            })}
                    </Text>
                </Modal>
            )}
        </div>
    )
}
export default styled(Roles)`
    ${styles}
`

const TeamHeader = styled.div`
    box-sizing: border-box;
    width: 100%;
    background: ${({ theme }) => theme.colors.surface.primary};
    border-radius: 8px;
    padding: 24px;
    max-height: 152px;

    .team-type-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${({ theme }) => theme.spacing[600]};

        .details {
            flex: 1;
            gap: ${({ theme }) => theme.spacing[100]};
        }
    }
`
